import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { CircularProgress, Modal, ModalDialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/joy';

import Card from 'components/core/Card';
import Stack from 'components/core/Stack';
import Sheet from 'components/core/Sheet';
import Table from 'components/core/Table';
import IconButton from 'components/core/IconButton';
import Typography from 'components/core/Typography';
import SortCarets from 'components/core/SortCarets';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import CommonUtils from 'utils/CommonUtils';
import lookupTableActions from 'actions/lookupTableActions';
import EditModal from 'components/EditModal';

const ValuesTable = ({ 
    tableName, 
    fields, 
    fieldLabels,
    values = [], 
    sorting,
    onSortChange,
    onLoadMore,
    hasMore,
    isLoading,
    hasView,
    canEdit,
    onEditComplete
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loadingElement, setLoadingElement] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ open: false, id: null, label: '' });
    const [editModal, setEditModal] = useState({ open: false, id: null });

    const getNestedValue = (obj, path) => {
        if (!obj || !path) return '';
        
        const keys = path.split('.');
        let value = obj;
        
        for (const key of keys) {
            if (value && typeof value.get === 'function') {
                value = value.get(key);
            } else if (value && typeof value === 'object') {
                value = value[key];
            } else {
                return '';
            }
        }
        
        return value || '';
    };

    useEffect(() => {
        if (!hasMore || !onLoadMore || !loadingElement) return;

        // Find the scrollable parent (the Sheet component)
        let scrollParent = loadingElement.parentElement;
        while (scrollParent && scrollParent !== document.body) {
            const style = window.getComputedStyle(scrollParent);
            if (style.overflow === 'auto' || style.overflow === 'scroll' || 
                style.overflowY === 'auto' || style.overflowY === 'scroll') {
                break;
            }
            scrollParent = scrollParent.parentElement;
        }
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isLoading && hasMore) {
                    onLoadMore();
                }
            },
            { 
                root: scrollParent,
                rootMargin: '0px',
                threshold: 0.1 
            }
        );

        observer.observe(loadingElement);
        
        return () => {
            observer.disconnect();
        };
    }, [hasMore, onLoadMore, isLoading, loadingElement]);

    const data = values.map(value => {
        const row = { id: value.id };
        fields.forEach(field => {
            row[field] = getNestedValue(value, field);
        });
        return row;
    });

    const handleSort = (field) => {
        if (onSortChange) {
            onSortChange(field);
        }
    };

    const handleView = (id) => {
        const routeMap = {
            'Event': `/event/${id}`,
            'Location': `/location/${id}`,
            'Taxonomy': `/taxonomy/${id}`,
            'Observation': `/observation/${id}`,
            'Evidence': `/evidence/${id}`
        };
        
        const route = routeMap[tableName];
        if (route) {
            navigate(route);
        }
    };

    const handleEdit = (id) => {
        if (tableName === 'Taxonomy') {
            navigate(`/taxonomy/${id}/edit`);
        } else if (tableName === 'Observation') {
            navigate(`/observation/${id}/edit`);
        } else if (tableName === 'Evidence') {
            navigate(`/evidence/${id}/edit`);
        } else {
            setEditModal({ open: true, id });
        }
    };

    const handleEditClose = () => {
        setEditModal({ open: false, id: null });
        if (onEditComplete) {
            onEditComplete();
        }
    };

    const handleDelete = (id, label) => {
        setDeleteModal({ open: true, id, label });
    };

    const confirmDelete = async () => {
        try {
            await dispatch(lookupTableActions.deleteRecord(tableName, deleteModal.id));
            setDeleteModal({ open: false, id: null, label: '' });
        } catch (error) {
            console.error('Failed to delete record:', error);
        }
    };

    const cancelDelete = () => {
        setDeleteModal({ open: false, id: null, label: '' });
    };

    const formatFieldName = (field) => {
        return field
            .split('.')
            .pop()
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    if (!values || values.length === 0) {
        return (
            <Card sx={{ p: 4, textAlign: 'center' }}>
                <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                    No {tableName} records found
                </Typography>
            </Card>
        );
    }

    return (
        <Card sx={{ p: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Sheet sx={{ 
                overflow: 'auto',
                borderRadius: 'sm',
                '--Table-lastColumnWidth': '100px',
                flex: 1,
                maxHeight: '100%',
            }}>
                <Table stickyHeader>
                    <thead>
                        <tr>
                            {fields.map((field) => (
                                <th key={field} style={{ 
                                    textAlign: 'left'
                                }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                            {fieldLabels?.[field] || formatFieldName(field)}
                                        </Typography>
                                        {sorting && (
                                            <SortCarets
                                                field={field}
                                                sorting={sorting}
                                                setSorting={() => handleSort(field)}
                                            />
                                        )}
                                    </Stack>
                                </th>
                            ))}
                            <th style={{ width: 'var(--Table-lastColumnWidth)', textAlign: 'center' }}>
                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                    Actions
                                </Typography>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row) => (
                            <tr key={row.id}>
                                {fields.map(field => (
                                    <td key={field}>
                                        <Typography level="body-sm">
                                            {(() => {
                                                const value = row[field];
                                                if (!value) return '-';
                                                
                                                const isDateField = field.toLowerCase().includes('date') || 
                                                                  field.toLowerCase().includes('createdat') || 
                                                                  field.toLowerCase().includes('updatedat');
                                                
                                                if (isDateField) {
                                                    const dateValue = new Date(value);
                                                    if (!isNaN(dateValue.getTime())) {
                                                        return CommonUtils.formatDate(value);
                                                    }
                                                }
                                                
                                                if (field === 'subjects' && Array.isArray(value)) {
                                                    return value.map(subject => 
                                                        subject?.get?.('shortCode') || subject?.shortCode || ''
                                                    ).filter(code => code).join(', ');
                                                }
                                                
                                                if (field === 'alternativeNames' && Array.isArray(value)) {
                                                    return value.join(', ');
                                                }
                                                
                                                return String(value);
                                            })()}
                                        </Typography>
                                    </td>
                                ))}
                                <td style={{ textAlign: 'center' }}>
                                    <Stack direction="row" spacing={1} justifyContent="center">
                                        {hasView && (
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                                onClick={() => handleView(row.id)}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        {canEdit && (
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                                onClick={() => handleEdit(row.id)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        <IconButton
                                            size="sm"
                                            variant="plain"
                                            color="danger"
                                            onClick={() => handleDelete(row.id, row[fields[0]] || 'this record')}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                {hasMore && (
                    <div 
                        ref={setLoadingElement}
                        style={{ 
                            padding: '16px', 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '60px'
                        }}
                    >
                        {isLoading && <CircularProgress size="sm" />}
                    </div>
                )}
            </Sheet>

            <Modal open={deleteModal.open} onClose={cancelDelete}>
                <ModalDialog variant="outlined" role="alertdialog">
                    <DialogTitle>
                        <WarningRoundedIcon />
                        Delete Confirmation
                    </DialogTitle>
                    <DialogContent>
                        Are you sure you want to delete "{deleteModal.label}"? This action cannot be undone.
                    </DialogContent>
                    <DialogActions>
                        <Button variant="solid" color="danger" onClick={confirmDelete}>
                            Delete
                        </Button>
                        <Button variant="plain" color="neutral" onClick={cancelDelete}>
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>

            {tableName !== 'Taxonomy' && tableName !== 'Observation' && (
                <EditModal
                    open={editModal.open}
                    onClose={handleEditClose}
                    className={tableName}
                    recordId={editModal.id}
                />
            )}
        </Card>
    );
};

ValuesTable.propTypes = {
    tableName: PropTypes.string.isRequired,
    fields: PropTypes.arrayOf(PropTypes.string).isRequired,
    values: PropTypes.array,
    sorting: PropTypes.object,
    onSortChange: PropTypes.func,
    onLoadMore: PropTypes.func,
    hasMore: PropTypes.bool,
    isLoading: PropTypes.bool,
    hasView: PropTypes.bool,
    canEdit: PropTypes.bool,
    onEditComplete: PropTypes.func
};

export default ValuesTable;